import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

type Ref = { personId: string; name: string };
type Mode = 'presencial' | 'llamada' | 'video';
type Slot = { id: string; counselor: Ref; startsAt: string; endsAt: string; mode: Mode; place: string | null; booked: boolean };
type Status = 'nueva' | 'propuesta' | 'agendada' | 'atendida' | 'rechazada' | 'cerrada' | 'cancelada';
type Request = {
  id: string; person: Ref; topic: string; message: string | null; preferred: string | null; status: Status; counselor: Ref | null; createdAt: string;
  appointment: Slot | null; notes: { author: Ref; body: string; createdAt: string }[] | null;
  urgent?: boolean; requestedStart?: string | null; requestedEnd?: string | null; proposedStart?: string | null; proposedEnd?: string | null; rejectReason?: string | null; phone?: string | null;
};
type Opening = { startsAt: string; endsAt: string };
type Hour = { id?: string; weekday: number; starts: string; ends: string };
type Overview = {
  canCounsel: boolean; canDelegate?: boolean; mine: Request[]; available: Slot[]; requests: Request[] | null; slots: Slot[] | null; counselors?: Ref[] | null;
  openings?: Opening[]; hours?: Hour[]; blocks?: { day: string; reason: string | null }[]; slotMinutes?: number | null;
};

export const TOPICS: [string, string][] = [
  ['pastoral', 'Pastoral'], ['matrimonio', 'Matrimonio'], ['familia', 'Familia'], ['oracion', 'Oración'], ['orientacion', 'Orientación'], ['otro', 'Otro'],
];
const topicLabel = (t: string) => TOPICS.find(([k]) => k === t)?.[1] ?? t;
const STATUS: Record<Status, string> = {
  nueva: 'En revisión', propuesta: 'Te propusieron otra fecha', agendada: 'Cita agendada', atendida: 'Atendida', rechazada: 'No aprobada', cerrada: 'Cerrada', cancelada: 'Cancelada',
};
export const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const dayLabel = (iso: string) => new Date(iso).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });
const hourLabel = (iso: string) => new Date(iso).toLocaleTimeString('es', { hour: 'numeric', minute: '2-digit' });
const range = (a: string, b: string) => `${dayLabel(a)}, ${hourLabel(a)} a ${hourLabel(b)}`;
const MODE: Record<Mode, string> = { presencial: 'En persona', llamada: 'Llamada', video: 'Videollamada' };
const when = (iso: string) => new Date(iso).toLocaleString('es', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
const slotLabel = (s: Slot) => `${when(s.startsAt)} · ${s.counselor.name} · ${MODE[s.mode]}${s.place ? ` (${s.place})` : ''}`;

/** Consejería y citas pastorales: el miembro pide (privado) y aparta horario; Pastoral atiende. */
export function CarePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/prayer/churches/${churchId}/care`;
  const data = useAsync((signal) => (churchId ? api.get<Overview>(base, signal) : Promise.resolve(null)), [base]);
  const [params, setParams] = useSearchParams();
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const o = data.data;
  if (!o) return null;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const tabs: [string, string][] = o.canCounsel
    ? [['mias', 'Para mí'], ['solicitudes', `Solicitudes (${o.requests?.filter((r) => r.status === 'nueva').length ?? 0})`], ['horarios', o.canDelegate ? 'Horario y agenda' : 'Mis horarios']]
    : [['mias', 'Para mí']];
  const tab = tabs.find(([k]) => k === params.get('ver'))?.[0] ?? (o.canCounsel ? 'solicitudes' : 'mias');

  return (
    <div className="page">
      <header className="page__header">
        <h1>Consejería</h1>
        <p className="muted">Pide una cita con Pastoral. Lo que escribas solo lo ven tú y Pastoral.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {tabs.length > 1 ? (
        <div className="tabs tabs--scroll" role="tablist" aria-label="Consejería">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ ver: k }, { replace: true })}>
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {tab === 'mias' ? (
        <>
          <UrgentButton busy={busy} onSend={(message) => run(() => api.post(`${base}/urgent`, { message }), 'Ya le avisamos al pastor. Te contactará cuanto antes.')} />
          <RequestForm available={o.available} openings={o.hours?.length ? o.openings ?? [] : null} busy={busy} onSave={(b) => run(() => api.post(`${base}/requests`, b), 'Solicitud enviada. Queda en revisión: Pastoral te avisará.')} />
          {o.mine.length ? (
            <Card title="Mis solicitudes">
              <ul className="list">
                {o.mine.map((r) => (
                  <li key={r.id} className="list__item list__item--stack">
                    <span>
                      <strong>{r.urgent ? '🚨 Urgente' : topicLabel(r.topic)}</strong> · {STATUS[r.status]}
                      {r.appointment ? <span className="small"> · {slotLabel(r.appointment)}</span> : r.requestedStart && r.requestedEnd && r.status === 'nueva' ? <span className="small"> · {range(r.requestedStart, r.requestedEnd)}</span> : null}
                    </span>
                    {r.rejectReason && (r.status === 'propuesta' || r.status === 'rechazada') ? <span className="small">{r.status === 'propuesta' ? 'Mensaje' : 'Motivo'}: {r.rejectReason}</span> : null}
                    {r.status === 'rechazada' ? <span className="small muted">Puedes pedir otra fecha cuando quieras.</span> : null}
                    {r.status === 'propuesta' && r.proposedStart && r.proposedEnd ? (
                      <span className="small">
                        <strong>Nueva fecha propuesta:</strong> {range(r.proposedStart, r.proposedEnd)}
                      </span>
                    ) : null}
                    <div className="actions">
                      {r.status === 'propuesta' ? (
                        <>
                          <Button busy={busy} onClick={() => void run(() => api.post(`${base}/requests/${r.id}/respond`, { accept: true }), 'Listo: tu cita quedó agendada.')}>Aceptar fecha</Button>
                          <Button variant="secondary" busy={busy} onClick={() => void run(() => api.post(`${base}/requests/${r.id}/respond`, { accept: false }), 'Avisamos que esa fecha no te queda.')}>No me queda</Button>
                        </>
                      ) : null}
                      {r.status === 'nueva' && o.available.length ? (
                        <select aria-label="Apartar horario" defaultValue="" disabled={busy} onChange={(e) => e.target.value && void run(() => api.post(`${base}/requests/${r.id}/book`, { slotId: e.target.value }), 'Cita apartada.')}>
                          <option value="">Apartar un horario…</option>
                          {o.available.map((s) => <option key={s.id} value={s.id}>{slotLabel(s)}</option>)}
                        </select>
                      ) : null}
                      {r.status === 'nueva' || r.status === 'agendada' ? (
                        <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Cancelar esta solicitud?')) void run(() => api.post(`${base}/requests/${r.id}/cancel`), 'Solicitud cancelada.'); }}>
                          Cancelar
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      ) : null}

      {tab === 'solicitudes' && o.canDelegate ? <CounselorsCard base={base} counselors={o.counselors ?? []} churchId={churchId} busy={busy} run={run} /> : null}
      {tab === 'solicitudes' && o.requests ? (
        o.requests.length === 0 ? (
          <Card><EmptyState title="No hay solicitudes de consejería." /></Card>
        ) : (
          o.requests.map((r) => <RequestCard key={r.id} r={r} slots={(o.slots ?? []).filter((s) => !s.booked)} base={base} busy={busy} run={run} counselors={o.canDelegate ? o.counselors ?? [] : null} />)
        )
      ) : null}

      {tab === 'horarios' && o.canDelegate ? <WeeklyHours base={base} hours={o.hours ?? []} slotMinutes={o.slotMinutes ?? null} busy={busy} run={run} /> : null}
      {tab === 'horarios' && o.canDelegate ? <BlockedDays base={base} blocks={o.blocks ?? []} busy={busy} run={run} /> : null}
      {tab === 'horarios' && o.slots ? <SlotsTab slots={o.slots} base={base} busy={busy} run={run} extra={!!o.canDelegate} /> : null}
    </div>
  );
}

function RequestForm({ available, openings, busy, onSave }: { available: Slot[]; openings: Opening[] | null; busy: boolean; onSave: (b: object) => Promise<boolean> }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!open) return <div><Button onClick={() => setOpen(true)}>Pedir consejería</Button></div>;
  const days = openings ? [...new Map(openings.map((x) => [new Date(x.startsAt).toDateString(), x.startsAt])).values()] : [];
  const times = openings ? openings.filter((x) => new Date(x.startsAt).toDateString() === day) : [];
  return (
    <Card title="Pedir consejería">
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const t = (k: string) => String(f.get(k) ?? '').trim() || null;
          if (openings && !t('startsAt')) return setError('Elige el día y la hora.');
          setError(null);
          void onSave({ topic: t('topic'), message: t('message'), preferred: t('preferred'), slotId: t('slotId'), startsAt: t('startsAt') }).then((ok) => ok && setOpen(false));
        }}
      >
        <div className="field">
          <label htmlFor="care-topic">Tema</label>
          <select id="care-topic" name="topic" defaultValue="pastoral">
            {TOPICS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="care-message">Cuéntanos brevemente (opcional)</label>
          <textarea id="care-message" name="message" rows={3} maxLength={2000} />
        </div>
        {error ? <Alert>{error}</Alert> : null}
        {openings ? (
          openings.length === 0 ? (
            <p className="muted small">Por ahora no hay horarios de consejería libres. Si es urgente, usa el botón de consejería urgente.</p>
          ) : (
            <div className="form__row">
              <div className="field">
                <label htmlFor="care-day">Día</label>
                <select id="care-day" value={day} onChange={(e) => setDay(e.target.value)}>
                  <option value="">Elige un día…</option>
                  {days.map((d) => <option key={d} value={new Date(d).toDateString()}>{dayLabel(d)}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="care-time">Horario</label>
                <select id="care-time" name="startsAt" defaultValue="" key={day} disabled={!day}>
                  <option value="">{day ? 'Elige la hora…' : 'Primero elige el día'}</option>
                  {times.map((x) => <option key={x.startsAt} value={x.startsAt}>{`${hourLabel(x.startsAt)} a ${hourLabel(x.endsAt)}`}</option>)}
                </select>
              </div>
            </div>
          )
        ) : null}
        {!openings && available.length ? (
          <div className="field">
            <label htmlFor="care-slot">Horario disponible (opcional)</label>
            <select id="care-slot" name="slotId" defaultValue="">
              <option value="">Que Pastoral me proponga uno</option>
              {available.map((s) => <option key={s.id} value={s.id}>{slotLabel(s)}</option>)}
            </select>
          </div>
        ) : null}
        {!openings ? <Field label="¿Cuándo te acomoda? (opcional)" name="preferred" maxLength={200} placeholder="Martes o jueves en la tarde" /> : null}
        <p className="small muted">Solo tú y Pastoral ven esta solicitud.</p>
        <div className="actions">
          <Button type="submit" busy={busy}>Enviar</Button>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
        </div>
      </form>
    </Card>
  );
}

function RequestCard({ r, slots, base, busy, run, counselors }: { r: Request; slots: Slot[]; base: string; busy: boolean; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean>; counselors: Ref[] | null }) {
  const [scheduling, setScheduling] = useState(false);
  // Responder sin aprobar: «Proponer otra fecha» (fecha y mensaje opcional) o «No puedo atenderla» (motivo).
  const [answer, setAnswer] = useState<'propose' | 'decline' | null>(null);
  const url = `${base}/requests/${r.id}`;
  function propose(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const start = String(f.get('altStart') ?? '');
    if (!start) return;
    const minutes = Number(f.get('altMinutes') ?? 60);
    const alternative = { startsAt: new Date(start).toISOString(), endsAt: new Date(new Date(start).getTime() + minutes * 60_000).toISOString() };
    void run(() => api.post(`${url}/reject`, { reason: String(f.get('message') ?? '').trim() || null, alternative }), 'Le propusimos la nueva fecha.').then((ok) => ok && setAnswer(null));
  }
  function decline(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const reason = String(new FormData(e.currentTarget).get('reason') ?? '').trim();
    void run(() => api.post(`${url}/reject`, { reason, alternative: null }), 'Le avisamos que no se puede atender.').then((ok) => ok && setAnswer(null));
  }
  const done = r.status === 'cancelada' || r.status === 'cerrada' || r.status === 'rechazada';
  function schedule(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const slotId = String(f.get('slotId') ?? '');
    const start = String(f.get('start') ?? '');
    const minutes = Number(f.get('minutes') ?? 60);
    const body = slotId
      ? { slotId }
      : { startsAt: new Date(start).toISOString(), endsAt: new Date(new Date(start).getTime() + minutes * 60_000).toISOString(), mode: String(f.get('mode')), place: String(f.get('place') ?? '').trim() || null };
    if (!slotId && !start) return;
    void run(() => api.post(`${url}/schedule`, body), 'Cita agendada. Le avisamos.').then((ok) => ok && setScheduling(false));
  }
  return (
    <Card
      title={`${r.urgent ? '🚨 URGENTE · ' : ''}${r.person.name} · ${topicLabel(r.topic)}`}
      action={<span className={`badge ${r.status === 'nueva' ? 'badge--warn' : 'badge--active'}`}>{STATUS[r.status] === 'Te propusieron otra fecha' ? 'Fecha propuesta' : STATUS[r.status]}</span>}
    >
      {r.message ? <p className="pre-line">{r.message}</p> : null}
      {r.phone ? <p className="small">Celular: <a href={`tel:${r.phone.replace(/\s+/g, '')}`}>{r.phone}</a></p> : null}
      {r.requestedStart && r.requestedEnd && r.status === 'nueva' ? <p><strong>Pide:</strong> {range(r.requestedStart, r.requestedEnd)}</p> : null}
      {r.status === 'propuesta' && r.proposedStart && r.proposedEnd ? <p className="small">Le propusiste: {range(r.proposedStart, r.proposedEnd)} (esperando su respuesta)</p> : null}
      {r.rejectReason && (r.status === 'propuesta' || r.status === 'rechazada') ? <p className="small muted">{r.status === 'propuesta' ? 'Mensaje' : 'Motivo'}: {r.rejectReason}</p> : null}
      {r.status === 'nueva' && r.requestedStart ? (
        <div className="actions actions--wrap">
          <Button busy={busy} onClick={() => void run(() => api.post(`${url}/approve`, {}), 'Aprobada. Ya está en su agenda y en la tuya.')}>Aprobar</Button>
          <Button variant="secondary" aria-expanded={answer === 'propose'} onClick={() => setAnswer(answer === 'propose' ? null : 'propose')}>Proponer otra fecha</Button>
          <Button variant="ghost" aria-expanded={answer === 'decline'} onClick={() => setAnswer(answer === 'decline' ? null : 'decline')}>No puedo atenderla</Button>
        </div>
      ) : null}
      {answer === 'propose' ? (
        <form className="form" onSubmit={propose}>
          <div className="form__row">
            <Field label="Nueva fecha y hora" name="altStart" type="datetime-local" required />
            <Field label="Minutos" name="altMinutes" type="number" min={15} max={240} defaultValue={60} />
          </div>
          <Field label="Mensaje (opcional)" name="message" maxLength={500} placeholder="Ese día salgo de viaje" />
          <div className="actions">
            <Button type="submit" busy={busy}>Proponer fecha</Button>
            <Button type="button" variant="ghost" onClick={() => setAnswer(null)}>Cancelar</Button>
          </div>
        </form>
      ) : null}
      {answer === 'decline' ? (
        <form className="form" onSubmit={decline}>
          <Field label="Motivo (lo verá la persona)" name="reason" required minLength={3} maxLength={500} />
          <div className="actions">
            <Button type="submit" variant="danger" busy={busy}>No puedo atenderla</Button>
            <Button type="button" variant="ghost" onClick={() => setAnswer(null)}>Cancelar</Button>
          </div>
        </form>
      ) : null}
      {r.preferred ? <p className="small">Le acomoda: {r.preferred}</p> : null}
      {r.appointment ? <p className="small"><strong>Cita:</strong> {slotLabel(r.appointment)}</p> : null}
      {counselors && !done ? (
        <div className="field">
          <label htmlFor={`assign-${r.id}`}>La atiende</label>
          <SearchSelect
            id={`assign-${r.id}`}
            value={r.counselor?.personId ?? ''}
            disabled={busy}
            emptyLabel="Pastoral"
            onChange={(v) => void run(() => api.put(`${url}/assign`, { counselorId: v || null }), 'Solicitud asignada.')}
            options={personOptions(r.counselor && !counselors.some((c) => c.personId === r.counselor!.personId) ? [r.counselor, ...counselors] : counselors)}
          />
        </div>
      ) : r.counselor ? <p className="small">La atiende: {r.counselor.name}</p> : null}
      {!done ? (
        <div className="actions actions--wrap">
          <Button variant="secondary" onClick={() => setScheduling(!scheduling)}>{r.appointment ? 'Cambiar cita' : 'Agendar cita'}</Button>
          {r.status === 'agendada' ? <Button variant="secondary" busy={busy} onClick={() => void run(() => api.put(`${url}/status`, { status: 'atendida' }), 'Marcada como atendida.')}>Marcar atendida</Button> : null}
          <Button variant="ghost" busy={busy} onClick={() => void run(() => api.put(`${url}/status`, { status: 'cerrada' }), 'Solicitud cerrada.')}>Cerrar</Button>
        </div>
      ) : null}
      {scheduling ? (
        <form className="form" onSubmit={schedule}>
          {slots.length ? (
            <div className="field">
              <label htmlFor={`slot-${r.id}`}>Uno de los horarios publicados</label>
              <select id={`slot-${r.id}`} name="slotId" defaultValue="">
                <option value="">— u otro horario (abajo) —</option>
                {slots.map((s) => <option key={s.id} value={s.id}>{slotLabel(s)}</option>)}
              </select>
            </div>
          ) : null}
          <div className="form__row form__row--3">
            <Field label="Fecha y hora" name="start" type="datetime-local" />
            <Field label="Minutos" name="minutes" type="number" min={15} max={240} defaultValue={60} />
            <div className="field">
              <label htmlFor={`mode-${r.id}`}>Cómo</label>
              <select id={`mode-${r.id}`} name="mode" defaultValue="presencial">
                {Object.entries(MODE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <Field label="Lugar (opcional)" name="place" maxLength={200} />
          <Button type="submit" busy={busy}>Agendar</Button>
        </form>
      ) : null}
      {r.notes ? (
        <>
          <h3 className="small">Notas pastorales (solo Pastoral)</h3>
          <ul className="list">
            {r.notes.map((n, i) => (
              <li key={i} className="list__item list__item--stack">
                <span className="small muted">{n.author.name} · {when(n.createdAt)}</span>
                <span className="pre-line">{n.body}</span>
              </li>
            ))}
          </ul>
          <form
            className="family-add"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const body = String(new FormData(form).get('note') ?? '').trim();
              if (body) void run(() => api.post(`${url}/notes`, { body }), 'Nota guardada.').then((ok) => ok && form.reset());
            }}
          >
            <Field label="Nueva nota" name="note" maxLength={4000} />
            <Button type="submit" variant="secondary" busy={busy}>Guardar nota</Button>
          </form>
        </>
      ) : null}
    </Card>
  );
}

/**
 * Citas sueltas por fecha. Con horario semanal (la forma principal) quedan como «Agregar horario extra»
 * para un día especial; quien solo atiende sin repartir la consejería las publica aquí.
 */
function SlotsTab({ slots, base, busy, run, extra }: { slots: Slot[]; base: string; busy: boolean; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean>; extra: boolean }) {
  const [repeat, setRepeat] = useState(false);
  const [open, setOpen] = useState(!extra);
  return (
    <>
      {extra && !open ? (
        <p>
          <Button variant="ghost" onClick={() => setOpen(true)}>
            + Agregar horario extra
          </Button>{' '}
          <span className="small muted">Para un día especial, fuera del horario semanal.</span>
        </p>
      ) : null}
      {open ? (
      <Card title={extra ? 'Horario extra' : 'Publicar horarios disponibles'}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const first = new Date(String(f.get('start')));
            if (Number.isNaN(first.getTime())) return;
            const minutes = Number(f.get('minutes') ?? 60);
            const count = Number(f.get('count') ?? 1);
            const weeks = repeat ? Math.min(Math.max(Number(f.get('weeks') ?? 1), 1), 12) : 1;
            const dates: { startsAt: string; endsAt: string }[] = [];
            for (let w = 0; w < weeks; w++) {
              for (let i = 0; i < Math.min(Math.max(count, 1), 8); i++) {
                const s = new Date(first.getTime() + w * 7 * 86400_000 + i * minutes * 60_000);
                dates.push({ startsAt: s.toISOString(), endsAt: new Date(s.getTime() + minutes * 60_000).toISOString() });
              }
            }
            void run(() => api.post(`${base}/slots`, { dates, mode: String(f.get('mode')), place: String(f.get('place') ?? '').trim() || null }), `${dates.length} horarios publicados.`);
          }}
        >
          <div className="form__row form__row--3">
            <Field label="Primer horario" name="start" type="datetime-local" required />
            <Field label="Minutos por cita" name="minutes" type="number" min={15} max={240} defaultValue={60} />
            <Field label="Citas seguidas" name="count" type="number" min={1} max={8} defaultValue={1} hint="Ej. 3 = tres citas una tras otra." />
          </div>
          <label className="check">
            <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} /> Repetir cada semana
          </label>
          {repeat ? <Field label="¿Cuántas semanas?" name="weeks" type="number" min={1} max={12} defaultValue={4} /> : null}
          <div className="form__row">
            <div className="field">
              <label htmlFor="slot-mode">Cómo</label>
              <select id="slot-mode" name="mode" defaultValue="presencial">
                {Object.entries(MODE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <Field label="Lugar (opcional)" name="place" maxLength={200} placeholder="Oficina pastoral" />
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>Publicar</Button>
            {extra ? <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button> : null}
          </div>
        </form>
      </Card>
      ) : null}
      <Card title={extra ? 'Horarios extra' : 'Próximos horarios'}>
        {slots.length === 0 ? <p className="muted small">No hay horarios publicados.</p> : null}
        <ul className="list">
          {slots.map((s) => (
            <li key={s.id} className="list__item">
              <span>{slotLabel(s)} {s.booked ? <span className="badge badge--active">Apartado</span> : null}</span>
              {!s.booked ? <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/slots/${s.id}`), 'Horario quitado.')}>Quitar</Button> : null}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/** Pastoral nombra consejeros y les pasa solicitudes. */
function CounselorsCard({ base, counselors, churchId, busy, run }: { base: string; counselors: Ref[]; churchId: string; busy: boolean; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean> }) {
  const members = useAsync((signal) => api.get<{ items: { personId: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items), [churchId]);
  const [pick, setPick] = useState('');
  const options = (members.status === 'ready' ? members.data : []).filter((m) => !counselors.some((c) => c.personId === m.personId));
  return (
    <Card title="Consejeros">
      <p className="muted small">Líderes, co-líderes o diáconos de confianza a quienes el pastor puede pasar solicitudes. Solo ven lo que se les asigna.</p>
      <ul className="list">
        {counselors.map((c) => (
          <li key={c.personId} className="list__item">
            <span>{c.name}</span>
            <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/counselors/${c.personId}`), 'Consejero quitado.')}>Quitar</Button>
          </li>
        ))}
      </ul>
      <div className="family-add">
        <div className="field">
          <label htmlFor="new-counselor">Nombrar consejero</label>
          <SearchSelect id="new-counselor" value={pick} onChange={setPick} options={options.map((m) => ({ value: m.personId, label: `${m.givenName} ${m.familyName}`.trim() }))} />
        </div>
        <Button variant="secondary" busy={busy} disabled={!pick} onClick={() => void run(() => api.post(`${base}/counselors`, { personId: pick }), 'Consejero nombrado.').then((ok) => ok && setPick(''))}>Nombrar</Button>
      </div>
    </Card>
  );
}

/** Botón rojo: avisa al instante al pastor (también al celular). */
function UrgentButton({ busy, onSend }: { busy: boolean; onSend: (message: string | null) => Promise<boolean> }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  if (!open) {
    return (
      <div className="care-urgent">
        <Button variant="danger" onClick={() => setOpen(true)}>🚨 Consejería urgente</Button>
        <span className="small muted">Para una crisis: le llega al pastor de inmediato.</span>
      </div>
    );
  }
  return (
    <Card title="🚨 Consejería urgente">
      <div className="field">
        <label htmlFor="urgent-msg">¿Qué pasa? (opcional)</label>
        <textarea id="urgent-msg" rows={2} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <p className="small muted">El pastor recibirá tu nombre, tu celular y este mensaje. Si tu vida o la de alguien corre peligro, llama también al 911.</p>
      <div className="actions">
        <Button variant="danger" busy={busy} onClick={() => void onSend(message.trim() || null).then((ok) => ok && setOpen(false))}>Avisar al pastor ahora</Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
      </div>
    </Card>
  );
}

/** Pastoral: días y horarios de consejería de cada semana (p. ej. lunes, jueves y sábado de 2 a 3 y de 5 a 6). */
function WeeklyHours({ base, hours, slotMinutes, busy, run }: { base: string; hours: Hour[]; slotMinutes: number | null; busy: boolean; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean> }) {
  const [minutes, setMinutes] = useState(slotMinutes ? String(slotMinutes) : '');
  const [days, setDays] = useState<number[]>(() => [...new Set(hours.map((h) => h.weekday))]);
  const [ranges, setRanges] = useState<{ starts: string; ends: string }[]>(() => {
    const unique = [...new Map(hours.map((h) => [`${h.starts}-${h.ends}`, { starts: h.starts, ends: h.ends }])).values()];
    return unique.length ? unique : [{ starts: '14:00', ends: '15:00' }];
  });
  const items = days.flatMap((weekday) => ranges.filter((x) => x.starts && x.ends).map((x) => ({ weekday, ...x })));
  return (
    <Card title="Horario de consejería">
      <p className="muted small">Elige los días y los horarios. Los miembros solo podrán pedir consejería en ellos (las próximas 4 semanas).</p>
      <fieldset className="weekday-picks">
        <legend>Días</legend>
        {[1, 2, 3, 4, 5, 6, 0].map((d) => (
          <label key={d} className="check">
            <input type="checkbox" checked={days.includes(d)} onChange={(e) => setDays((l) => (e.target.checked ? [...l, d] : l.filter((x) => x !== d)))} /> {WEEKDAYS[d]}
          </label>
        ))}
      </fieldset>
      {ranges.map((x, i) => (
        <div key={i} className="form__row form__row--3">
          <Field label={`Horario ${i + 1}: desde`} type="time" value={x.starts} onChange={(e) => setRanges((l) => l.map((y, j) => (j === i ? { ...y, starts: e.target.value } : y)))} />
          <Field label="hasta" type="time" value={x.ends} onChange={(e) => setRanges((l) => l.map((y, j) => (j === i ? { ...y, ends: e.target.value } : y)))} />
          {ranges.length > 1 ? <div className="field"><Button variant="ghost" onClick={() => setRanges((l) => l.filter((_, j) => j !== i))}>Quitar</Button></div> : null}
        </div>
      ))}
      <div className="field">
        <label htmlFor="care-minutes">Duración de cada cita</label>
        <select id="care-minutes" value={minutes} onChange={(e) => setMinutes(e.target.value)}>
          <option value="">Una cita por horario</option>
          {[15, 20, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m} minutos</option>)}
        </select>
      </div>
      <div className="actions actions--wrap">
        <Button variant="secondary" onClick={() => setRanges((l) => [...l, { starts: '17:00', ends: '18:00' }])}>+ Otro horario</Button>
        <Button busy={busy} onClick={() => void run(() => api.put(`${base}/hours`, { items, slotMinutes: minutes ? Number(minutes) : null }), items.length ? 'Horario guardado.' : 'Horario quitado.')}>Guardar horario</Button>
      </div>
      {hours.length ? (
        <p className="small muted">
          Actual: {[1, 2, 3, 4, 5, 6, 0].filter((d) => hours.some((h) => h.weekday === d)).map((d) => `${WEEKDAYS[d]} ${hours.filter((h) => h.weekday === d).map((h) => `${h.starts}–${h.ends}`).join(' y ')}`).join(' · ')}
        </p>
      ) : null}
    </Card>
  );
}

/** Pastoral: días sin consejería. */
function BlockedDays({ base, blocks, busy, run }: { base: string; blocks: { day: string; reason: string | null }[]; busy: boolean; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean> }) {
  return (
    <Card title="Fechas bloqueadas">
      <p className="muted small">Ese día no se ofrecen horarios de consejería.</p>
      <form
        className="family-add"
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.currentTarget;
          const f = new FormData(form);
          const day = String(f.get('day') ?? '');
          if (day) void run(() => api.put(`${base}/blocks/${day}`, { reason: String(f.get('reason') ?? '').trim() || null }), 'Fecha bloqueada.').then((ok) => ok && form.reset());
        }}
      >
        <Field label="Fecha" name="day" type="date" required />
        <Field label="Motivo (opcional)" name="reason" maxLength={200} placeholder="Retiro, viaje…" />
        <Button type="submit" variant="secondary" busy={busy}>Bloquear</Button>
      </form>
      <ul className="list">
        {blocks.map((b) => (
          <li key={b.day} className="list__item">
            <span>{new Date(`${b.day}T12:00:00`).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })}{b.reason ? <span className="small muted"> · {b.reason}</span> : null}</span>
            <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/blocks/${b.day}`), 'Fecha desbloqueada.')}>Quitar</Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
